import { useForm, useFieldArray } from "react-hook-form";
import {
  PostCandidateAthlete,
  registerAthleteWithEventId,
} from "../../../services/AthleteService";
import { useAxiosInstance } from "../../../services/Interceptor";
import { toast } from "react-toastify";
import Nav from "../../../components/Nav";
import { useParams, useLocation, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { GetEventsByEventId } from "../../../services/EventsService";
import {
  CheckValidCashToken,
  PostPayment,
} from "../../../services/PaymentService";
import { GetEventCombinePrice } from "../../../services/EventsService";
import { useDispatch } from "react-redux";
import {
  registerAccountStore,
  clearRegisterData,
} from "../../../store/slices/accountSlice";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../../../components/LoadingSpinner";
import {
  positions,
  states,
  tshirtSizes,
} from "../../../utils/PositionStateData";
import { BackButton } from "../../../components/backButton";
import { CheckIsEmailExist } from "../../../services/AccountService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { useTrackEvent } from "../../../hooks/useTrackEvent";

const RegisterAthlete = () => {
  const { eventId } = useParams();
  const location = useLocation();
  const apiInstance = useAxiosInstance();
  const [eventNameSelected, setEventNameSelected] = useState();
  const [isRegistered, setIsRegistered] = useState(false);
  const [cashTokenNew, setCashTokenNew] = useState("");
  const [combinePrice, setCombinePrice] = useState(0);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const primaryPositions = positions.filter((p) => p !== "None");
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [showUniversities, setShowUniversities] = useState(true);
  const trackEvent = useTrackEvent();

  useEffect(() => {
    trackEvent("Registration0_EventsPage", { event_id: eventId });
  }, []); // eslint-disable-line

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const data = await GetEventsByEventId(apiInstance, eventId);

        if (data) {
          setEventNameSelected(`${data.date} - ${data.name} - ${data.city}`);
          trackEvent("Registration1_OpenForm", {
            event_id: eventId,
            event_name: data.name,
            event_date: data.date,
          });
        }
      } catch (err) {
        console.error(err);
      }
    };

    loadEvents();
  }, [apiInstance, eventId, trackEvent]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    const cashToken = searchParams.get("cashToken");

    if (cashToken) {
      setCashTokenNew(cashToken);
      trackEvent("Registration3_5_AppliedCoupon", { event_id: eventId });
    }
  }, [location.search, eventId, trackEvent]);

  useEffect(() => {
    const fetchPricingOptions = async () => {
      try {
        const response = await GetEventCombinePrice(apiInstance, eventId);

        if (response.data) {
          setCombinePrice(response?.data?.combinePrice);
          trackEvent("Registration3_ViewedPricing", {
            event_id: eventId,
            combine_price: response?.data?.combinePrice,
          });
        }
      } catch (error) {}
    };

    fetchPricingOptions();
  }, []); // eslint-disable-line

  const validateCashToken = async (token) => {
    try {
      const response = await CheckValidCashToken(apiInstance, token);

      if (response.success === true) {
        return true;
      } else {
        toast.error("Invalid cash token.", { position: "top-center" });

        return false;
      }
    } catch (error) {
      toast.error("Failed to validate cash token.", { position: "top-center" });
    }
  };

  const checkIsEmailExist = async (email) => {
    try {
      const response = await CheckIsEmailExist(apiInstance, email);

      if (response?.data.data === false) {
        toast.warning("Email ID is already registered!", {
          position: "top-center",
        });

        return true;
      }

      return false;
    } catch (error) {
      toast.error("Failed to validate email address.", {
        position: "top-center",
      });

      return true;
    }
  };

  const {
    register,

    handleSubmit,

    reset,

    watch,

    control,

    trigger,
    setValue,

    formState: { errors },
  } = useForm({
    defaultValues: {
      firstName: "",

      lastName: "",

      email: "",

      confirmEmail: "",

      password: "",

      confirmPassword: "",

      age: 0,

      gpa: "",

      highSchool: "",

      expectedYearOfGraduation: "",

      primaryPosition: "",

      secondaryPosition: "",

      sportsParticipation: null,

      city: "",

      state: "",

      universityOfInteres: "",

      academicDegreeOfInteres: "",

      role: "spotcombine",

      athletePhoneNumber: "",

      parentPhoneNumber: "",

      varsityYears: null,

      offersReceived: null,

      athleteOffers: null,

      hasGraduated: false,

      major: "",

      yearsLeftToPlay: "",

      redShirtAvailable: false,

      personHelpingYouName: "",

      personHelpingYouRelationship: "",

      personHelpingYouPhone: "",
      universitiesAttended: [{ year: "", school: "", className: "" }],
      tShirtSize: "",
    },

    mode: "onChange",

    reValidateMode: "onChange",

    criteriaMode: "all",
  });

  const selectedState = watch("state");

  const selectedPosition = watch("primaryPosition");

  const hasGraduated = watch("hasGraduated");

  useEffect(() => {
    if (!hasGraduated) {
      setValue("major", ""); // Clear the Major field
    }
  }, [hasGraduated, setValue]);

  const { fields, append, remove } = useFieldArray({
    control,

    name: "universitiesAttended",
  });

  const onSubmit = async (data) => {
    setSubmitAttempted(true);

    // Trigger validation for all fields

    const isFormValid = await trigger(undefined, { shouldFocus: true });

    if (!isFormValid) {
      toast.error("Please fix all validation errors before submitting.", {
        position: "top-center",
      });

      return;
    }

    const stripPosition = (position) => {
      if (!position) return "";

      return position.includes("(") ? position.split("(")[0].trim() : position;
    };

    const {
      confirmEmail: _ce,
      confirmPassword: _cp,
      ...submittableData
    } = data;

    const sanitizedData = {
      ...submittableData,

      age: Number(data.age) || 0,

      expectedYearOfGraduation: Number(data.expectedYearOfGraduation) || 0,

      varsityYears: Number(data.varsityYears) || 0,

      universitiesAttended: (data.universitiesAttended || [])
        .filter(
          (u) => u.school?.trim() && u.year?.trim() && u.className?.trim(),
        )
        .map((u) => ({
          ...u,
          year: Number(u.year) || 0,
          school: u.school.trim(),
          className: u.className.trim(),
        })),

      athleteOffers: Array.isArray(data.athleteOffers)
        ? data.athleteOffers
        : [],

      sportsParticipation: data.sportsParticipation || "",

      primaryPosition: stripPosition(
        data.primaryPosition === "None" ? "" : data.primaryPosition,
      ),

      secondaryPosition: stripPosition(
        data.secondaryPosition === "None" ? "" : data.secondaryPosition,
      ),

      yearsLeftToPlay: data.yearsLeftToPlay
        ? parseInt(data.yearsLeftToPlay)
        : null,

      redShirtAvailable: Boolean(data.redShirtAvailable),

      hasGraduated: data.hasGraduated,
      major: hasGraduated ? data.major : "",
    };

    const isEmailExist = await checkIsEmailExist(sanitizedData.email);

    if (isEmailExist) {
      setLoading(false);

      return;
    }

    await PostCandidateAthlete(apiInstance, {
      firstName: sanitizedData.firstName,
      lastName: sanitizedData.lastName,
      email: sanitizedData.email,
      highSchool: sanitizedData.highSchool,
      expectedYearOfGraduation: sanitizedData.expectedYearOfGraduation,
      primaryPosition: sanitizedData.primaryPosition,
      athletePhoneNumber: sanitizedData.athletePhoneNumber,
      parentPhoneNumber: sanitizedData.parentPhoneNumber,
      city: sanitizedData.city,
      state: sanitizedData.state,
      eventId: eventId,
    })
      .then(() => {
        trackEvent("Registration2_CompletedForm", {
          event_id: eventId,
        });
      })
      .catch(() => {});

    setLoading(true);

    try {
      if (cashTokenNew) {
        const isValid = await validateCashToken(cashTokenNew);

        if (!isValid) {
          toast.error("Invalid or expired cash token.", {
            position: "top-right",
          });

          return;
        }

        const registrationResponse = await registerAthleteWithEventId(
          apiInstance,

          sanitizedData,

          eventId,
        );

        if (registrationResponse.status === 200) {
          const userId = registrationResponse.data.id;

          const paymentPayload = {
            userId: userId,

            amount: combinePrice,

            isCombine: true,

            isPositionDrill: false,

            isAnnualSubscription: false,

            isMonthlySubscription: false,

            isCash: true,

            eventId: eventId,

            transactionId: "",

            intellipayToken: "",
          };

          await PostPayment(apiInstance, paymentPayload);

          trackEvent("Registration5_RegistrationCompleted", {
            event_id: eventId,
            payment_method: "cash",
            amount: combinePrice,
          });
          setLoading(false);

          setTimeout(() => navigate("/register/registrationsuccess"), 3000);

          setIsRegistered(true);

          reset();
        } else {
          trackEvent("Registration4_5_PaymentFailed", {
            event_id: eventId,
            reason: registrationResponse?.error ?? "unknown",
          });
          toast.error("Registration failed: " + registrationResponse.error, {
            position: "top-right",
          });

          return;
        }
      } else {
        const updatedData = { ...sanitizedData, eventId: eventId };

        dispatch(clearRegisterData());

        dispatch(registerAccountStore(updatedData));

        localStorage.clear();

        localStorage.setItem("registeredAccount", JSON.stringify(updatedData));

        trackEvent("Registration4_OpenedPaymentModal", { event_id: eventId });
        setLoading(false);

        navigate("/athlete-membership", { state: { fromSpotRegister: true } });

        reset();

        toast.success("Please complete the Payment.", {
          position: "top-right",
        });
      }
    } catch (error) {
      toast.error("Error Registering combine Athlete: " + error.error, {
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to check if field should show error

  const shouldShowError = (fieldName) => {
    return submitAttempted || errors[fieldName];
  };

  return (
    <>
      <Nav />

      {!isRegistered && (
        <div className="w-full max-w-5xl p-6 mx-auto pt-10">
          <div className=" hidden"> {!cashTokenNew && <BackButton />}</div>

          {/* Info area */}
          <div className="alert flex flex-row items-center bg-red-200 p-3 rounded border-b-2 border-red-300 mt-3 hidden">
            <div className="alert-icon flex items-center bg-red-100 border-2 border-red-500 justify-center h-8 w-8 flex-shrink-0 rounded-full">
              <span className="text-red-500">
                <svg
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  className="h-6 w-6"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  ></path>
                </svg>
              </span>
            </div>
            <div className="alert-content ml-4">
              <div className="alert-title font-semibold text-sm text-red-700">
                Step 1 Register for the Portal Combine
              </div>
              <div className="alert-description text-xs text-red-500">
                Fill out the form below to register for the January event.
              </div>
              <div className="alert-description text-xs text-red-500">
                &nbsp;
              </div>
              <div className="alert-title font-semibold text-sm text-red-700">
                Step 2 Claim your FLX ID
              </div>
              <div className="alert-description text-xs text-red-500">
                This is your ticket to the Portal Combine. <br></br>
                After registration, you'll see a link to start, or complete your
                FLX ID. <br></br>
                [For identify verification and security, some of the questions
                will be the same]<br></br>
                At the Combine, you will receive a digital FLX Link to wear as a
                wristband, containing all your FLX ID information. After the
                event, it will be updated with all new mesaurables from the
                Portal Combine.
              </div>
            </div>
          </div>
          <div className="text-[#06D1E8] py-4 text-2xl font-bold my-3 pt-4">
            {eventNameSelected} Combine Registration
          </div>

          {/* Info area */}

          <div
            className="alert flex flex-row items-center bg-blue-100 border-t border-b border-blue-500 text-blue-700 px-4 py-3 my-4 rounded-md"
            role="alert"
          >
            <div className="alert-icon flex items-center bg-blue-100 border-2 border-blue-500 justify-center h-7 w-7 flex-shrink-0 rounded-full">
              <span className="text-blue-500">
                <svg
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  className="h-5 w-5"
                >
                  5
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  ></path>
                </svg>
              </span>
            </div>
            <div className="alert-content ml-4 w-full">
              <div className="alert-title font-semibold text-lg text-blue-700">
                Already registered for a previous combine?
              </div>
              <div className="alert-description text-sm text-blue-700">
                Please
                <Link
                  className="text-blue-700 font-semibold hover:underline px-1"
                  to="/sign-in"
                >
                  Sign In
                </Link>
                to your account, select this event from your dashboard and click
                Register.
              </div>
              <div className="alert-title font-semibold text-base text-blue-700 mt-3 pt-1 border-t border-blue-300">
                Event details will be sent after registration.
              </div>
            </div>
          </div>

          <form
            className={`athleteregistration-${process.env.REACT_APP_ENVIRONMENT_NAME} container mx-auto `}
            onSubmit={handleSubmit(onSubmit)}
            noValidate
          >
            <h2 className="text-lg font-bold mb-2 text-white border-b pb-2 border-gray-700">
              Personal Details
            </h2>
            <div className="grid sm:grid-cols-2 gap-x-6">
              <div className="form-field mt-4">
                <label htmlFor="firstName">First Name</label>
                <input
                  {...register("firstName", {
                    required: "First name is required",
                  })}
                  placeholder="Enter your first name"
                />

                {shouldShowError("firstName") && errors.firstName && (
                  <span className="text-red-500 text-sm">
                    {errors.firstName?.message}
                  </span>
                )}
              </div>
              <div className="form-field mt-4">
                <label htmlFor="lastName">Last Name</label>
                <input
                  type="text"
                  {...register("lastName", {
                    required: "Last name is required",
                  })}
                  placeholder="Enter your last name"
                />

                {shouldShowError("lastName") && errors.lastName && (
                  <span className="text-red-500 text-sm">
                    {errors.lastName?.message}
                  </span>
                )}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-1 xl:gap-8 lg:gap-8 mt-4">
              <div className="form-field mt-0">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  {...register("email", {
                    required: "Email is required",

                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,

                      message: "Invalid email address",
                    },
                  })}
                  placeholder="Enter your email"
                />

                {shouldShowError("email") && errors.email && (
                  <span className="text-red-500 text-sm">
                    {errors.email?.message}
                  </span>
                )}
              </div>
              <div className="form-field mt-0">
                <label htmlFor="confirmEmail">Confirm Email</label>
                <input
                  type="email"
                  {...register("confirmEmail", {
                    required: "Please confirm your email",
                    validate: (value, formValues) =>
                      value === formValues.email ||
                      "Email addresses do not match",
                  })}
                  placeholder="Re-enter your email"
                />
                {shouldShowError("confirmEmail") && errors.confirmEmail && (
                  <span className="text-red-500 text-sm">
                    {errors.confirmEmail?.message}
                  </span>
                )}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-1 xl:gap-8 lg:gap-8 mt-4">
              <div className="form-field mt-0">
                <label htmlFor="Password">Password</label>
                <input
                  type="password"
                  aria-invalid={errors.password ? "true" : "false"}
                  name="password"
                  {...register("password", {
                    required: "Password is required",
                  })}
                  placeholder="Enter password"
                />

                {shouldShowError("password") && errors.password && (
                  <span className="text-red-500 text-sm">
                    {errors.password?.message}
                  </span>
                )}
              </div>
              <div className="form-field mt-0">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  type="password"
                  {...register("confirmPassword", {
                    required: "Please confirm your password",
                    validate: (value, formValues) =>
                      value === formValues.password || "Passwords do not match",
                  })}
                  placeholder="Re-enter your password"
                />
                {shouldShowError("confirmPassword") &&
                  errors.confirmPassword && (
                    <span className="text-red-500 text-sm">
                      {errors.confirmPassword?.message}
                    </span>
                  )}
              </div>
            </div>
            <div className="grid sm:grid-cols-2  gap-x-6">
              <div className="form-field mt-4">
                <label htmlFor="age">Age</label>
                <input
                  type="number"
                  name="age"
                  min="0"
                  {...register("age", {
                    min: {
                      value: 0,
                      message: "Age cannot be negative",
                    },
                  })}
                  placeholder="Enter your age"
                />

                {shouldShowError("age") && errors.age && (
                  <span className="text-red-500 text-sm">
                    {errors.age?.message}
                  </span>
                )}
              </div>
              <div className="form-field mt-4">
                <label htmlFor="athletePhoneNumber">Athlete Phone Number</label>
                <input
                  type="tel"
                  id="athletePhoneNumber"
                  inputMode="numeric"
                  pattern="[0-9]{10}"
                  placeholder="Enter athlete's phone number"
                  maxLength={15}
                  {...register("athletePhoneNumber", {
                    required: "Athlete Phone Number is required",

                    pattern: {
                      value: /^[0-9]{10}$/,

                      message: "Enter a valid 10-digit phone number",
                    },
                  })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onInput={(e) => {
                    e.target.value = e.target.value.replace(/\D/g, "");
                  }}
                />

                {shouldShowError("athletePhoneNumber") &&
                  errors.athletePhoneNumber && (
                    <span className="text-red-500 text-sm">
                      {errors.athletePhoneNumber?.message}
                    </span>
                  )}
              </div>
            </div>
            <div className="grid sm:grid-cols-2  gap-x-6 mt-4">
              <div className="form-field mt-0">
                <label htmlFor="city">Hometown City</label>
                <input
                  type="text"
                  name="city"
                  {...register("city", { required: "City is required" })}
                  placeholder="Enter your hometown"
                ></input>

                {shouldShowError("city") && errors.city && (
                  <span className="text-red-500 text-sm">
                    {errors.city?.message}
                  </span>
                )}
              </div>
              <div className="form-field mt-0 relative">
                <label htmlFor="state">State</label>
                <select
                  id="state"
                  name="state"
                  className={`

      bg-gray-50 border border-gray-300 text-md rounded-md 

      pr-10 appearance-none w-full

      ${selectedState ? "text-gray-900" : "text-gray-400"}

    `}
                  {...register("state", { required: "State is required" })}
                >
                  <option value="" disabled>
                    Select your state
                  </option>

                  {states.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>

                {/* Arrow Icon */}
                <svg
                  className="w-5 h-5 text-gray-200 absolute right-3 top-2/3 -translate-y-1/2 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>

                {shouldShowError("state") && errors.state && (
                  <span className="text-red-500 text-sm">
                    {errors.state?.message}
                  </span>
                )}
              </div>
            </div>

            {/* Education Details*/}
            <div className="mt-8">
              <h2 className="text-lg font-bold mb-2 text-white border-b border-gray-700">
                Education Details
              </h2>
              <div className="grid sm:grid-cols-2  gap-x-6">
                <div className="form-field mt-4">
                  <label htmlFor="highSchool">
                    What High School did you graduate from?
                  </label>
                  <input
                    type="text"
                    name="highSchool"
                    {...register("highSchool", {
                      required: "High School is required",
                    })}
                    placeholder="Enter high school name"
                  />

                  {shouldShowError("highSchool") && errors.highSchool && (
                    <span className="text-red-500 text-sm">
                      {errors.highSchool?.message}
                    </span>
                  )}
                </div>
                <div className="form-field mt-4">
                  <label htmlFor="expectedYearOfGraduation">
                    High School Graduation Year
                  </label>
                  <input
                    type="number"
                    className="no-spinner"
                    name="expectedYearOfGraduation"
                    {...register("expectedYearOfGraduation")}
                    placeholder="Enter graduation year"
                  />
                  {shouldShowError("expectedYearOfGraduation") &&
                    errors.expectedYearOfGraduation && (
                      <span className="text-red-500 text-sm">
                        {errors.expectedYearOfGraduation?.message}
                      </span>
                    )}
                </div>
              </div>
              <div className="grid sm:grid-cols-2  gap-x-6">
                <div className="form-field mt-4">
                  <label htmlFor="universityOfInteres">
                    Universities you are Most Interested in Playing for
                  </label>
                  <input
                    type="text"
                    name="universityOfInteres"
                    {...register("universityOfInteres", {
                      required: "University of interest is required",
                    })}
                    placeholder="Enter names of universities"
                  />

                  {shouldShowError("universityOfInteres") &&
                    errors.universityOfInteres && (
                      <span className="text-red-500 text-sm">
                        {errors.universityOfInteres?.message}
                      </span>
                    )}
                </div>
                <div className="form-field mt-4">
                  <label htmlFor="AcademicInterest">
                    Academic Degree of Interest
                  </label>
                  <input
                    type="text"
                    name="academicDegreeOfInteres"
                    {...register("academicDegreeOfInteres", {
                      required: "Academic Degree of Interest is required",
                    })}
                    placeholder="Academic Degree of Interest"
                  ></input>

                  {shouldShowError("academicDegreeOfInteres") &&
                    errors.academicDegreeOfInteres && (
                      <span className="text-red-500 text-sm">
                        {errors.academicDegreeOfInteres?.message}
                      </span>
                    )}
                </div>
              </div>

              {/* New Graduation Status and Major Fields */}
              <div className="grid sm:grid-cols-3 gap-x-6 mt-4">
                <div className="form-field relative">
                  <label
                    htmlFor="hasGraduated"
                    className="block text-base text-gray-900"
                  >
                    Have you graduated?
                  </label>
                  <select
                    id="hasGraduated"
                    defaultValue=""
                    {...register("hasGraduated", {
                      setValueAs: (v) =>
                        v === "true" ? true : v === "false" ? false : false,
                    })}
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>

                  {shouldShowError("hasGraduated") && errors.hasGraduated && (
                    <span className="text-red-500 text-sm">
                      {errors.hasGraduated.message}
                    </span>
                  )}
                </div>
                <div className="form-field">
                  <label htmlFor="major">Major</label>
                  <input
                    type="text"
                    id="major"
                    {...register("major")}
                    placeholder="Major (if graduated)"
                    disabled={!hasGraduated}
                    className={
                      !hasGraduated ? "bg-gray-100 cursor-not-allowed" : ""
                    }
                  />

                  {shouldShowError("major") && errors.major && (
                    <span className="text-red-500 text-sm">
                      {errors.major?.message}
                    </span>
                  )}
                </div>
                <div className="form-field mt-0">
                  <label htmlFor="gpa">GPA</label>
                  <input
                    type="text"
                    name="gpa"
                    {...register("gpa")}
                    placeholder="Enter GPA"
                  />

                  {shouldShowError("gpa") && errors.gpa && (
                    <span className="text-red-500 text-sm">
                      {errors.gpa?.message}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-field pt-5">
                <label htmlFor="universitiesAttended">
                  Universities Attended
                  <button
                    type="button"
                    onClick={() => setShowUniversities(!showUniversities)}
                    className="text-gray-500 hover:text-gray-700 ml-8"
                  >
                    <FontAwesomeIcon
                      icon={showUniversities ? faChevronUp : faChevronDown}
                    />
                  </button>
                </label>
                {showUniversities && (
                  <div className="mt-2">
                    {fields.map((field, index) => (
                      <div
                        key={field.id}
                        className="flex items-center gap-4 mb-2"
                      >
                        <div className="flex flex-col mb-2">
                          <input
                            type="text"
                            {...register(
                              `universitiesAttended.${index}.school`,
                              { required: "University Name is required" },
                            )}
                            placeholder="University Name"
                            className="border border-gray-300 rounded-md p-2 w-96"
                          />
                          {errors.universitiesAttended?.[index]?.school && (
                            <span className="text-red-500 text-sm">
                              {
                                errors.universitiesAttended[index].school
                                  .message
                              }
                            </span>
                          )}
                        </div>

                        <div className="flex flex-col mb-2">
                          <input
                            type="text"
                            {...register(`universitiesAttended.${index}.year`, {
                              required: "Year is required",
                            })}
                            placeholder="Year"
                            className="border border-gray-300 rounded-md p-2 w-40"
                          />
                          {errors.universitiesAttended?.[index]?.year && (
                            <span className="text-red-500 text-sm">
                              {errors.universitiesAttended[index].year.message}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-col mb-2">
                          <input
                            type="text"
                            {...register(
                              `universitiesAttended.${index}.className`,
                              { required: "Class is required" },
                            )}
                            placeholder="Class (ex: Freshman)"
                            className="border border-gray-300 rounded-md p-2 w-60"
                          />
                          {errors.universitiesAttended?.[index]?.className && (
                            <span className="text-red-500 text-sm">
                              {
                                errors.universitiesAttended[index].className
                                  .message
                              }
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => remove(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() =>
                        append({ year: "", school: "", className: "" })
                      }
                      className="text-blue-500 hover:text-blue-700"
                    >
                      Add University
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Athletic Information*/}
            <div className="mt-8">
              <h2 className="text-lg font-bold mb-4 text-white border-b pb-2 border-gray-700 ">
                Athletic Information
              </h2>
              <div className="grid sm:grid-cols-2 gap-1 xl:gap-8 lg:gap-8 mt-4">
                <div className="form-field mt-0 relative">
                  <label
                    htmlFor="primaryPosition"
                    className="block text-base text-gray-900 dark:text-white"
                  >
                    Primary Position
                  </label>
                  <select
                    id="primaryPosition"
                    {...register("primaryPosition", {
                      required: "Primary Position is required",
                    })}
                    className={`bg-gray-50 border border-gray-300 text-md rounded-md w-full pr-10 appearance-none bg-no-repeat ${
                      selectedPosition ? "text-gray-900" : "text-gray-400"
                    }`}
                    style={{
                      backgroundImage:
                        "url(\"data:image/svg+xml,%3Csvg fill='none' stroke='%236b7280' stroke-width='4' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E\")",

                      backgroundPosition: "right 0.75rem center",

                      backgroundSize: "1.1rem",

                      backgroundRepeat: "no-repeat",
                    }}
                  >
                    <option
                      value=""
                      disabled
                      className="text-white bg-gray-800"
                    >
                      Select primary position
                    </option>

                    {primaryPositions.map((position) => (
                      <option
                        key={position}
                        value={position}
                        className="text-white bg-gray-800"
                      >
                        {position === "Interior Offensive Line"
                          ? "Interior Offensive Line (Guard or Center)"
                          : position}
                      </option>
                    ))}
                  </select>

                  {shouldShowError("primaryPosition") &&
                    errors.primaryPosition && (
                      <span className="text-red-500 text-sm">
                        {errors.primaryPosition.message}
                      </span>
                    )}
                </div>

                <div className="form-field mt-0 relative">
                  <label
                    htmlFor="secondaryPosition"
                    className="block text-base text-gray-900 dark:text-white"
                  >
                    Secondary Position
                  </label>
                  <select
                    id="secondaryPosition"
                    className={`bg-gray-50 border border-gray-300 text-md rounded-md w-full pr-10 appearance-none bg-no-repeat ${
                      watch("secondaryPosition")
                        ? "text-gray-900"
                        : "text-gray-400"
                    }`}
                    style={{
                      backgroundImage:
                        "url(\"data:image/svg+xml,%3Csvg fill='none' stroke='%236b7280' stroke-width='3' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E\")",

                      backgroundPosition: "right 0.75rem center",

                      backgroundSize: "1.1rem",

                      backgroundRepeat: "no-repeat",
                    }}
                    {...register("secondaryPosition")}
                  >
                    <option
                      value=""
                      disabled
                      className="text-white bg-gray-800"
                    >
                      None
                    </option>

                    {positions.map((position) => (
                      <option
                        key={position}
                        value={position}
                        className="text-white bg-gray-800"
                      >
                        {position === "Interior Offensive Line"
                          ? "Interior Offensive Line (Guard or Center)"
                          : position}
                      </option>
                    ))}
                  </select>

                  {shouldShowError("secondaryPosition") &&
                    errors.secondaryPosition && (
                      <span className="text-red-500 text-sm">
                        {errors.secondaryPosition.message}
                      </span>
                    )}
                </div>
                <div className="form-field mt-0 col-span-2">
                  <label htmlFor="tShirtSize">T-Shirt Size</label>
                  <select
                    id="tShirtSize"
                    {...register("tShirtSize", {
                      required: "T-Shirt Size is required",
                    })}
                    className={`bg-gray-50 border border-gray-300 text-md rounded-md w-full pr-10 appearance-none bg-no-repeat ${
                      watch("secondaryPosition")
                        ? "text-gray-900"
                        : "text-gray-400"
                    }`}
                    style={{
                      backgroundImage:
                        "url(\"data:image/svg+xml,%3Csvg fill='none' stroke='%236b7280' stroke-width='3' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E\")",

                      backgroundPosition: "right 0.75rem center",

                      backgroundSize: "1.1rem",

                      backgroundRepeat: "no-repeat",
                    }}
                  >
                    <option value="" disabled className="text-gray-400">
                      Select T-Shirt Size
                    </option>

                    {tshirtSizes.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                  {errors.tShirtSize && (
                    <span className="text-red-500">
                      {errors.tShirtSize.message}
                    </span>
                  )}
                </div>
              </div>

              {/* New Eligibility Section */}
              <div className="grid sm:grid-cols-2 gap-1 xl:gap-8 lg:gap-8 mt-4">
                <div className="form-field mt-0 relative">
                  <label
                    htmlFor="yearsLeftToPlay"
                    className="block text-base text-gray-900"
                  >
                    Years of eligibility left
                  </label>
                  <select
                    id="yearsLeftToPlay"
                    {...register("yearsLeftToPlay")}
                    className="bg-gray-50 border border-gray-300 text-md rounded-md

     form-selectw appearance-none bg-no-repeat text-gray-900 

     w-full pr-10"
                    defaultValue=""
                  >
                    <option value="" disabled className="text-gray-400">
                      Select eligibility
                    </option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                  </select>

                  {/* Dropdown Arrow */}
                  <svg
                    className="w-5 h-5 text-gray-200 absolute right-3 top-[65%] -translate-y-1/2 pointer-events-none"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>

                  {shouldShowError("yearsLeftToPlay") &&
                    errors.yearsLeftToPlay && (
                      <span className="text-red-500 text-sm">
                        {errors.yearsLeftToPlay.message}
                      </span>
                    )}
                </div>
                <div className="form-field mt-0 relative">
                  <label
                    htmlFor="redShirtAvailable"
                    className="block text-base text-gray-900"
                  >
                    Redshirt Available?
                  </label>
                  <select
                    id="redShirtAvailable"
                    {...register("redShirtAvailable")}
                    className="bg-gray-50 border border-gray-300 text-md rounded-md

               form-selectw appearance-none bg-no-repeat text-gray-900 

               w-full pr-10"
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>

                  {/* Dropdown Arrow */}
                  <svg
                    className="w-5 h-5 text-gray-200 absolute right-3 top-[65%] -translate-y-1/2 pointer-events-none"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>

                  {shouldShowError("redShirtAvailable") &&
                    errors.redShirtAvailable && (
                      <span className="text-red-500 text-sm">
                        {errors.redShirtAvailable.message}
                      </span>
                    )}
                </div>
              </div>
            </div>

            {/* Parent / Guardian Details*/}
            <div className="mt-8">
              <h2 className="text-lg font-bold mb-2 text-white border-b pb-2 border-gray-700">
                Parent / Guardian Details
              </h2>
              <div className="grid sm:grid-cols-2 gap-1 xl:gap-8 lg:gap-8 mt-4">
                <div className="form-field">
                  <label htmlFor="parentPhoneNumber">Parent Phone Number</label>
                  <input
                    type="tel"
                    id="parentPhoneNumber"
                    inputMode="numeric"
                    pattern="[0-9]{10}"
                    placeholder="Enter parent phone number (optional)"
                    maxLength={15}
                    {...register("parentPhoneNumber", {
                      pattern: {
                        value: /^[0-9]{10}$/,

                        message: "Enter a valid 10-digit phone number",
                      },
                    })}
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                    }}
                  />

                  {shouldShowError("parentPhoneNumber") &&
                    errors.parentPhoneNumber && (
                      <span className="text-red-500 text-sm">
                        {errors.parentPhoneNumber?.message}
                      </span>
                    )}
                </div>
              </div>
            </div>

            {/* Person helping you most with your Decision*/}
            <div className="mt-8">
              <h2 className="text-lg font-bold mb-2 text-white border-b pb-2 border-gray-700">
                Person helping you most with your Decision
              </h2>
              <div className="grid sm:grid-cols-3 gap-1 xl:gap-8 lg:gap-8 mt-4">
                <div className="form-field">
                  <label htmlFor="personHelpingYouName">Name</label>
                  <input
                    type="text"
                    id="personHelpingYouName"
                    autoComplete="off"
                    {...register("personHelpingYouName")}
                    placeholder="Enter advisor's name"
                  />

                  {shouldShowError("personHelpingYouName") &&
                    errors.personHelpingYouName && (
                      <span className="text-red-500 text-sm">
                        {errors.personHelpingYouName?.message}
                      </span>
                    )}
                </div>
                <div className="form-field">
                  <label htmlFor="personHelpingYouRelationship">
                    Relationship to you
                  </label>
                  <input
                    type="text"
                    id="personHelpingYouRelationship"
                    {...register("personHelpingYouRelationship")}
                    placeholder="Enter relationship"
                  />

                  {shouldShowError("personHelpingYouRelationship") &&
                    errors.personHelpingYouRelationship && (
                      <span className="text-red-500 text-sm">
                        {errors.personHelpingYouRelationship?.message}
                      </span>
                    )}
                </div>
                <div className="form-field">
                  <label htmlFor="personHelpingYouPhone">Phone Number</label>
                  <input
                    type="tel"
                    id="personHelpingYouPhone"
                    inputMode="numeric"
                    pattern="[0-9]{10}"
                    placeholder="Enter phone number"
                    maxLength={15}
                    {...register("personHelpingYouPhone", {
                      pattern: {
                        value: /^[0-9]{10}$/,

                        message: "Enter a valid 10-digit phone number",
                      },
                    })}
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                    }}
                  />

                  {shouldShowError("personHelpingYouPhone") &&
                    errors.personHelpingYouPhone && (
                      <span className="text-red-500 text-sm">
                        {errors.personHelpingYouPhone?.message}
                      </span>
                    )}
                </div>
              </div>
            </div>
            <div className="mt-10 p-3 rounded-md bg-[#2d2d2d]">
              <h2 className="text-base font-semibold mb-2 text-white">
                Waiver & Liability Agreement
              </h2>
              {/* Scrollable Text Box */}
              <div className="h-40 overflow-y-scroll border border-gray-600 p-3 bg-black rounded-md text-sm leading-6 text-white">
                <p className="text-sm">
                  I hereby acknowledge that I am, or my child is, voluntarily
                  participating in a Combine organized by Track It Verified, LLC
                  (“the Activity”). In consideration for being permitted to
                  participate in this activity, I hereby agree as follows:
                </p>
                <ol className="list-decimal list-inside text-sm mt-2">
                  <li className="pb-3">
                    {" "}
                    I understand and acknowledge that there are certain risks
                    and dangers associated with the Activity, including but not
                    limited to: getting injured while performing the combine
                    drills.{" "}
                  </li>
                  <li className="pb-3">
                    On behalf of myself (if 18 or older) or on behalf of my
                    child, I/we voluntarily assume all risks related to
                    participation in the Activity and agree to release and hold
                    harmless Track It Verified, LLC, its officers, employees,
                    volunteers, and agents from any and all claims, damages,
                    liabilities, costs, and expenses that may arise as a result
                    of participation in the Activity, including but not limited
                    to injury or loss.{" "}
                  </li>
                  <li className="pb-3">
                    On behalf of myself (if 18 or older) or on behalf of my
                    child, I/we agree to indemnify and defend Track It Verified,
                    LLC, its officers, employees, volunteers, and agents against
                    any and all claims, damages, liabilities, costs, and
                    expenses, including attorney fees, arising from or in
                    connection with participation in the Activity.{" "}
                  </li>
                  <li className="pb-3">
                    On behalf of myself (if 18 or older) or on behalf of my
                    child, I/we certify that I or my child is in good health and
                    have/has no physical or mental conditions that prevent me or
                    my child from safely participating in the Activity.{" "}
                  </li>
                  <li className="pb-3">
                    On behalf of myself (if 18 or older) or on behalf of my
                    child, I grant Track It Verified, LLC the right to use
                    photographs, video recordings, or other media of me or my
                    child taken during the Activity for promotional or marketing
                    purposes. If I am signing this on behalf of my child, I
                    understand that Pursuant to the Children’s Online Policy
                    Protection Act (“COPPA”), Track It Verified, LLC, located at
                    1899 Oak Lane, Provo, Utah 84604, wishes to collect personal
                    information from my child. The “personal information” of the
                    Child includes, images, photographs, video recordings, name
                    acknowledgment, video tapings, statistics, height, weight
                    and other information pertaining to the Child while
                    participating in the Activity. I understand that
                    Parent/Guardian consent is required for the collection, use
                    and disclosure of the child's personal information. I hereby
                    acknowledge that I am the Parent/Guardian of the child
                    listed above and consent to Track It Verified, LLC's
                    collection, use, and disclosure of the child's personal
                    information by signing this document. I understand that I
                    have the option to consent to the collection and use of the
                    child's personal information without consenting to
                    disclosure of the child's information to third parties. I
                    understand that my failure to consent to Track It Verified,
                    LLC's collection use and disclosure of the child's personal
                    information will not prevent the child from participating in
                    the Activity. Track It Verified, LLC, will not require a
                    child to disclose more information than is reasonably
                    necessary to participate in the Activity as a condition of
                    participation. Parent/Guardian can review the child's
                    personal information, ask to have it deleted, and refuse to
                    allow any further collection use or disclosure of the
                    child's information. Track It Verified, LLC has established
                    and maintains reasonable procedures to protect the security
                    and integrity of the personal information collected from the
                    child. If the Parent/Guardian does not wish to provide
                    consent for Track It Verified, LLC's collection, use, and
                    disclosure of the child's personal information or seeks to
                    revoke previously granted consent, Parent/Guardian must
                    contact Track It Verified, LLC at:
                    trackittverified@gmail.com and make this request directly.
                    Parent/Guardian releases Track It Verified, LLC, its
                    employees, contractors, and each and all persons and parties
                    involved from any liability connected with the taking,
                    recording, or publication of said photographs, video, or
                    computer images involving the Child. Parent/Guardian waives
                    all rights he/she may have to any claims for any payment or
                    royalties in connection with any exhibition, televising, or
                    other publication of these materials, regardless of the
                    purpose or sponsoring of such exhibiting, broadcasting, or
                    other publication. All negatives and positives, whether
                    prints, digital video, film or video, are the exclusive
                    property of Track It Verified, LLC or the person or entity
                    designated by it, solely and completely.
                  </li>
                </ol>
                <p className="mt-1 text-sm">
                  I HAVE READ THIS RELEASE, IF SIGNING MYSELF OR ON BEHALF OF MY
                  CHILD, AND FULLY UNDERSTAND ITS TERMS, AND UNDERSTAND THAT I
                  AM SIGNING FREELY AND VOLUNTARILY WITHOUT ANY INDUCEMENT.
                </p>
              </div>

              {/* Checkbox Requirement */}
              <div className="flex items-center gap-2 my-3">
                <input
                  type="checkbox"
                  {...register("agreedToWaiver", { required: true })}
                  className="w-4 h-4"
                />
                <label className="text-sm text-white">
                  I have read and agree to the Waiver & Liability Agreement.
                </label>
              </div>

              {errors.agreedToWaiver && (
                <p className="text-red-500 text-xs mt-1">
                  You must accept the waiver before registering.
                </p>
              )}
            </div>
            <div className="px-2 my-10">
              <div className="form-field mt-4">
                {loading && <LoadingSpinner />}
              </div>
              <button
                type="submit"
                className="bg-red-500 text-white uppercase rounded-full px-8 py-3 text-center mt-4 hover:bg-red-700 cursor-pointer w-full"
              >
                Registration
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default RegisterAthlete;
